import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Shield, UserPlus, Building2, Loader2, CheckCircle, AlertCircle, Mail } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../contexts/AuthContext';
import API_URL from '../lib/apiUrl';

const JoinInvitation = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  
  const [step, setStep] = useState('loading'); // loading, details, confirm, otp, error
  const [invitation, setInvitation] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Form state
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');

  useEffect(() => {
    const validateInvitation = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/invitation/${token}`);
        setInvitation(response.data);
        setName(response.data.name);
        setEmail(response.data.email);
        setStep('details');
      } catch (err) {
        setError(err.response?.data?.detail || 'Invalid or expired invitation link');
        setStep('error');
      }
    };
    
    validateInvitation();
  }, [token]);

  const handleAcceptInvitation = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Accept invitation and verify email domain
      const response = await axios.post(`${API_URL}/api/invitation/${token}/accept`, {
        email: email,
        name: name
      });
      
      // Check if we should redirect to login
      if (response.data.redirect_to_login) {
        toast.success('Email verified! Redirecting to login page...');
        // Short delay to show the success message
        setTimeout(() => {
          navigate('/auth', { state: { email: email, fromInvitation: true } });
        }, 1500);
      } else {
        // Fallback to OTP flow if needed
        toast.success('Verification code sent to your email!');
        setStep('otp');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to accept invitation');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/auth/verify-otp`, {
        email: email,
        otp: otp
      });

      // Login the user
      login(response.data.token, response.data.user);
      toast.success('Welcome! You have successfully joined the organization.');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (step === 'loading') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-slate-400 mx-auto mb-4" />
          <p className="text-slate-600">Validating invitation...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (step === 'error') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center"
        >
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="h-8 w-8 text-red-600" />
          </div>
          <h1 className="font-heading font-bold text-2xl text-slate-900 mb-3">
            Invalid Invitation
          </h1>
          <p className="text-slate-600 mb-6">{error}</p>
          <Button onClick={() => navigate('/')} variant="outline">
            Go to Home
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex">
      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 p-12 flex-col justify-between">
        <div className="flex items-center space-x-3">
          <Shield className="h-10 w-10 text-white" />
          <span className="font-heading font-bold text-2xl text-white">AIVORA Comply360</span>
        </div>
        
        <div>
          <h2 className="font-heading font-bold text-4xl text-white mb-4">
            Join Your Team
          </h2>
          <p className="text-slate-300 text-lg">
            You&apos;ve been invited to collaborate on ISO 27001:2022 maturity assessments. 
            Complete your verification to access the platform.
          </p>
        </div>
        
        <p className="text-slate-500 text-sm">
          © {new Date().getFullYear()} AIVORA Techlabs Global Pvt Ltd
        </p>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12 bg-white lg:rounded-l-3xl">
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          {/* Mobile logo */}
          <div className="flex items-center space-x-3 mb-8 lg:hidden">
            <Shield className="h-10 w-10 text-slate-900" />
            <span className="font-heading font-bold text-2xl text-slate-900">AIVORA Comply360</span>
          </div>

          {step === 'details' && invitation && (
            <>
              <div className="mb-8">
                <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <UserPlus className="h-7 w-7 text-green-600" />
                </div>
                <h1 className="font-heading font-bold text-3xl text-slate-900 mb-2">
                  You&apos;re Invited!
                </h1>
                <p className="text-slate-600">
                  <strong>{invitation.inviter_name}</strong> has invited you to join <strong>{invitation.org_name}</strong>
                </p>
              </div>

              {/* Invitation details card */}
              <div className="bg-slate-50 rounded-xl p-5 mb-6 border border-slate-200">
                <div className="flex items-center gap-3 mb-4">
                  <Building2 className="h-5 w-5 text-slate-500" />
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wide">Organization</p>
                    <p className="font-semibold text-slate-900">{invitation.org_name}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Shield className="h-5 w-5 text-slate-500" />
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wide">Your Role</p>
                    <p className="font-semibold text-slate-900">{invitation.role}</p>
                  </div>
                </div>
              </div>

              <form onSubmit={handleAcceptInvitation} className="space-y-5">
                <div>
                  <label className="font-mono text-xs uppercase tracking-widest text-slate-500 mb-2 block">
                    Full Name
                  </label>
                  <Input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your full name"
                    className="py-5 text-base"
                    required
                  />
                </div>

                <div>
                  <label className="font-mono text-xs uppercase tracking-widest text-slate-500 mb-2 block">
                    Email Address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3.5 h-5 w-5 text-slate-400" />
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="your.email@company.com"
                      className="pl-10 py-5 text-base bg-slate-50"
                      required
                      readOnly
                    />
                  </div>
                  <p className="text-xs text-slate-500 mt-1.5">
                    This invitation was sent to this email address
                  </p>
                </div>

                <Button 
                  type="submit" 
                  className="w-full py-6 text-base font-semibold"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="mr-2 h-5 w-5" />
                      Accept Invitation & Continue
                    </>
                  )}
                </Button>
              </form>

              <p className="text-center text-sm text-slate-500 mt-6">
                Continue to set up your account using the invited email address.
              </p>
            </>
          )}

          {step === 'otp' && (
            <>
              <div className="mb-8">
                <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                  <Mail className="h-7 w-7 text-blue-600" />
                </div>
                <h1 className="font-heading font-bold text-3xl text-slate-900 mb-2">
                  Verify Your Email
                </h1>
                <p className="text-slate-600">
                  We&apos;ve sent a 6-digit verification code to <strong>{email}</strong>
                </p>
              </div>

              <form onSubmit={handleVerifyOtp} className="space-y-5">
                <div>
                  <label className="font-mono text-xs uppercase tracking-widest text-slate-500 mb-2 block">
                    Verification Code
                  </label>
                  <Input
                    type="text"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="Enter 6-digit code"
                    className="py-6 text-center text-2xl tracking-widest font-mono"
                    maxLength={6}
                    required
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full py-6 text-base font-semibold"
                  disabled={loading || otp.length !== 6}
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    'Verify & Join'
                  )}
                </Button>
              </form>

              <button 
                onClick={() => setStep('details')}
                className="w-full text-center text-sm text-slate-500 mt-6 hover:text-slate-700"
              >
                ← Go back
              </button>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default JoinInvitation;
